import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, ChevronLeft, Plus, Minus, X, Package, Users, Download, Truck } from 'lucide-react';
import Swal from 'sweetalert2';

const API_BASE_URL = "https://backend.dlapakistan.com/api";

const Checkout = () => {
  const navigate = useNavigate();
  const [basket, setBasket] = useState({ items: [], total_items: 0, subtotal: 0, total_shipping: 0, grand_total: 0 });
  const [initialLoading, setInitialLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updatingItems, setUpdatingItems] = useState({});
  const [isMember, setIsMember] = useState(false);
  const [memberToken, setMemberToken] = useState(null);
  const [memberData, setMemberData] = useState(null);

  const GREEN = "#70B444";
  const GREEN_DARK = "#5A9437";
  const GREEN_LIGHT = "#F0F7EC";

  // Function to get CSRF token from cookies
  const getCsrfToken = useCallback(() => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrftoken') {
        return value;
      }
    }
    return '';
  }, []);

  // Create headers for fetch requests
  const createHeaders = useCallback(() => {
    const csrfToken = getCsrfToken();
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-CSRFToken': csrfToken,
    };
    
    if (memberToken) {
      headers['Authorization'] = `Bearer ${memberToken}`;
    }
    
    return headers;
  }, [memberToken, getCsrfToken]);

  useEffect(() => {
    const token = localStorage.getItem("member_token");
    const memberDataStr = localStorage.getItem("member_data");
    setIsMember(!!(token && memberDataStr));
    setMemberToken(token);
    if (memberDataStr) {
      try {
        setMemberData(JSON.parse(memberDataStr));
      } catch (e) {
        console.error("Error parsing member data:", e);
      }
    }
  }, []);

  const ensureSingleSession = useCallback(async () => {
    try {
      const headers = createHeaders();
      
      const response = await fetch(`${API_BASE_URL}/dla-shop/basket/count/`, {
        method: 'GET',
        credentials: 'include',
        headers: headers
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log("✅ Session initialized, count:", data.count);
        return true;
      }
    } catch (error) {
      console.error("❌ Session error:", error);
    }
    return false;
  }, [createHeaders]);

  const fetchBasket = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setIsLoading(true);
    }
    
    try {
      const headers = createHeaders();
      
      const response = await fetch(`${API_BASE_URL}/dla-shop/basket/my_basket/`, {
        method: 'GET',
        credentials: 'include',
        headers: headers
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log("📦 Basket data:", data);
      
      // Calculate totals manually to ensure free items are properly accounted for
      let calculatedSubtotal = 0;
      let calculatedTotalShipping = 0;
      
      const processedItems = (data.items || []).map(item => {
        // Check if this item should be free for members
        const isItemFree = isMember && (
          item.is_free_for_members_at_add === true || 
          item.product_is_free_for_members === true
        );
        
        const price = isItemFree ? 0 : parseFloat(item.price_at_add || 0);
        const shipping = parseFloat(item.shipping_at_add || 0);
        const quantity = item.quantity || 1;
        
        calculatedSubtotal += price * quantity;
        calculatedTotalShipping += shipping * quantity;
        
        // Add a flag to the item for display purposes
        return {
          ...item,
          _isFreeForMember: isItemFree
        };
      });
      
      setBasket({
        items: processedItems,
        total_items: data.total_items || 0,
        subtotal: calculatedSubtotal,
        total_shipping: calculatedTotalShipping,
        grand_total: calculatedSubtotal + calculatedTotalShipping
      });
      
      setError(null);
      
    } catch (error) {
      console.error("❌ Error:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
      setInitialLoading(false);
    }
  }, [createHeaders, isMember]);

  const getItemPrice = useCallback((item) => {
    // Check multiple possible flags for free member items
    const isItemFree = isMember && (
      item._isFreeForMember === true ||
      item.is_free_for_members_at_add === true || 
      item.product_is_free_for_members === true
    );
    
    if (isItemFree) {
      return 0;
    }
    return parseFloat(item.price_at_add || 0);
  }, [isMember]);

  const getItemShipping = useCallback((item) => {
    return parseFloat(item.shipping_at_add || 0);
  }, []);

  const isItemFreeForMember = useCallback((item) => {
    return isMember && (
      item._isFreeForMember === true ||
      item.is_free_for_members_at_add === true || 
      item.product_is_free_for_members === true
    );
  }, [isMember]);

  // Memoized calculations for performance
  const displaySubtotal = useMemo(() => {
    return basket.items.reduce((total, item) => {
      const price = getItemPrice(item);
      return total + (price * (item.quantity || 1));
    }, 0);
  }, [basket.items, getItemPrice]);

  const displayTotalShipping = useMemo(() => {
    return basket.items.reduce((total, item) => {
      const shipping = getItemShipping(item);
      return total + (shipping * (item.quantity || 1));
    }, 0);
  }, [basket.items, getItemShipping]);

  const displayGrandTotal = useMemo(() => {
    return displaySubtotal + displayTotalShipping;
  }, [displaySubtotal, displayTotalShipping]);

  const memberSavings = useMemo(() => {
    return basket.items.reduce((savings, item) => {
      if (isItemFreeForMember(item)) {
        return savings + (parseFloat(item.price_at_add || 0) * (item.quantity || 1));
      }
      return savings;
    }, 0);
  }, [basket.items, isItemFreeForMember]);

  // Optimistic update function for quantity changes
  const updateQuantityOptimistic = useCallback(async (itemId, newQty, currentItem) => {
    if (newQty < 1) return;
    
    // Mark this item as updating
    setUpdatingItems(prev => ({ ...prev, [itemId]: true }));
    
    // Store original item data for rollback
    const originalQuantity = currentItem.quantity;
    
    // Optimistically update UI - immediate visual feedback
    setBasket(prevBasket => {
      const updatedItems = prevBasket.items.map(item => 
        item.id === itemId ? { ...item, quantity: newQty } : item
      );
      
      // Recalculate totals
      let newSubtotal = 0;
      let newTotalShipping = 0;
      
      updatedItems.forEach(item => {
        const price = getItemPrice(item);
        const shipping = getItemShipping(item);
        const qty = item.quantity;
        newSubtotal += price * qty;
        newTotalShipping += shipping * qty;
      });
      
      return {
        ...prevBasket,
        items: updatedItems,
        subtotal: newSubtotal,
        total_shipping: newTotalShipping,
        grand_total: newSubtotal + newTotalShipping
      };
    });
    
    try {
      const headers = createHeaders();
      
      const response = await fetch(`${API_BASE_URL}/dla-shop/basket/update_item/`, {
        method: 'POST',
        credentials: 'include',
        headers: headers,
        body: JSON.stringify({ item_id: itemId, quantity: newQty })
      });
      
      if (!response.ok) {
        throw new Error('Update failed');
      }
      
      const data = await response.json();
      
      // Silently sync with server data (no loading state)
      if (data.items) {
        let newSubtotal = 0;
        let newTotalShipping = 0;
        
        const processedItems = data.items.map(item => {
          const isItemFree = isMember && (
            item.is_free_for_members_at_add === true || 
            item.product_is_free_for_members === true
          );
          
          const price = isItemFree ? 0 : parseFloat(item.price_at_add || 0);
          const shipping = parseFloat(item.shipping_at_add || 0);
          const quantity = item.quantity || 1;
          
          newSubtotal += price * quantity;
          newTotalShipping += shipping * quantity;
          
          return {
            ...item,
            _isFreeForMember: isItemFree
          };
        });
        
        setBasket({
          items: processedItems,
          total_items: data.total_items || 0,
          subtotal: newSubtotal,
          total_shipping: newTotalShipping,
          grand_total: newSubtotal + newTotalShipping
        });
      }
      
      // Update basket count in header (background)
      fetch(`${API_BASE_URL}/dla-shop/basket/count/`, {
        credentials: 'include',
        headers: headers
      }).then(res => res.json()).then(countData => {
        window.dispatchEvent(new CustomEvent('basketUpdated', { 
          detail: { count: countData.count || 0 } 
        }));
      }).catch(console.error);
      
    } catch (error) {
      console.error("Error updating:", error);
      
      // Revert optimistic update on error
      setBasket(prevBasket => {
        const revertedItems = prevBasket.items.map(item => 
          item.id === itemId ? { ...item, quantity: originalQuantity } : item
        );
        
        let newSubtotal = 0;
        let newTotalShipping = 0;
        
        revertedItems.forEach(item => {
          const price = getItemPrice(item);
          const shipping = getItemShipping(item);
          const qty = item.quantity;
          newSubtotal += price * qty;
          newTotalShipping += shipping * qty;
        });
        
        return {
          ...prevBasket,
          items: revertedItems,
          subtotal: newSubtotal,
          total_shipping: newTotalShipping,
          grand_total: newSubtotal + newTotalShipping
        };
      });
      
      // Show error but don't block UI
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: 'Failed to update quantity. Please try again.',
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      });
    } finally {
      // Clear updating state for this item
      setUpdatingItems(prev => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
    }
  }, [createHeaders, getItemPrice, getItemShipping, isMember]);

  const removeItemOptimistic = useCallback(async (itemId, currentItem) => {
    // Mark this item as updating
    setUpdatingItems(prev => ({ ...prev, [itemId]: true }));
    
    // Store original items for rollback
    const originalItems = [...basket.items];
    
    // Optimistically remove item from UI
    setBasket(prevBasket => {
      const filteredItems = prevBasket.items.filter(item => item.id !== itemId);
      
      let newSubtotal = 0;
      let newTotalShipping = 0;
      
      filteredItems.forEach(item => {
        const price = getItemPrice(item);
        const shipping = getItemShipping(item);
        const qty = item.quantity;
        newSubtotal += price * qty;
        newTotalShipping += shipping * qty;
      });
      
      return {
        ...prevBasket,
        items: filteredItems,
        total_items: filteredItems.length,
        subtotal: newSubtotal,
        total_shipping: newTotalShipping,
        grand_total: newSubtotal + newTotalShipping
      };
    });
    
    try {
      const headers = createHeaders();
      
      const response = await fetch(`${API_BASE_URL}/dla-shop/basket/remove_item/`, {
        method: 'POST',
        credentials: 'include',
        headers: headers,
        body: JSON.stringify({ item_id: itemId })
      });
      
      if (!response.ok) {
        throw new Error('Remove failed');
      }
      
      const data = await response.json();
      
      if (data.items) {
        let newSubtotal = 0;
        let newTotalShipping = 0;
        
        const processedItems = data.items.map(item => {
          const isItemFree = isMember && (
            item.is_free_for_members_at_add === true || 
            item.product_is_free_for_members === true
          );
          
          const price = isItemFree ? 0 : parseFloat(item.price_at_add || 0);
          const shipping = parseFloat(item.shipping_at_add || 0);
          const quantity = item.quantity || 1;
          
          newSubtotal += price * quantity;
          newTotalShipping += shipping * quantity;
          
          return {
            ...item,
            _isFreeForMember: isItemFree
          };
        });
        
        setBasket({
          items: processedItems,
          total_items: data.total_items || 0,
          subtotal: newSubtotal,
          total_shipping: newTotalShipping,
          grand_total: newSubtotal + newTotalShipping
        });
      }
      
      // Update basket count in header
      fetch(`${API_BASE_URL}/dla-shop/basket/count/`, {
        credentials: 'include',
        headers: headers
      }).then(res => res.json()).then(countData => {
        window.dispatchEvent(new CustomEvent('basketUpdated', { 
          detail: { count: countData.count || 0 } 
        }));
      }).catch(console.error);
      
      // Show success toast
      Swal.fire({
        icon: 'success',
        title: 'Removed',
        text: 'Item removed from basket',
        timer: 1000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      });
      
    } catch (error) {
      console.error("Error removing:", error);
      
      // Revert optimistic removal on error
      setBasket(prevBasket => {
        let newSubtotal = 0;
        let newTotalShipping = 0;
        
        originalItems.forEach(item => {
          const price = getItemPrice(item);
          const shipping = getItemShipping(item);
          const qty = item.quantity;
          newSubtotal += price * qty;
          newTotalShipping += shipping * qty;
        });
        
        return {
          ...prevBasket,
          items: originalItems,
          total_items: originalItems.length,
          subtotal: newSubtotal,
          total_shipping: newTotalShipping,
          grand_total: newSubtotal + newTotalShipping
        };
      });
      
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to remove item. Please try again.',
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      });
    } finally {
      // Clear updating state for this item
      setUpdatingItems(prev => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
    }
  }, [basket.items, createHeaders, getItemPrice, getItemShipping, isMember]);

  // Initial load
  useEffect(() => {
    let isMounted = true;
    
    const init = async () => {
      await ensureSingleSession();
      if (isMounted) {
        await fetchBasket(true);
      }
    };
    
    init();
    
    return () => {
      isMounted = false;
    };
  }, [ensureSingleSession, fetchBasket]);

  // Handle basket updates from other components
  useEffect(() => {
    const handleBasketUpdate = () => {
      fetchBasket(false); // Silent update
    };
    
    window.addEventListener('basketUpdated', handleBasketUpdate);
    return () => window.removeEventListener('basketUpdated', handleBasketUpdate);
  }, [fetchBasket]);

  const formatPrice = (price) => `$${parseFloat(price || 0).toFixed(2)}`;

  // Show initial loading only first time
  if (initialLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#70B444]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-red-600 mb-4">{error}</p>
        <button 
          onClick={() => fetchBasket(true)} 
          className="bg-[#70B444] text-white px-6 py-2 rounded hover:bg-[#5A9437]"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!basket.items || basket.items.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Your cart is empty</h2>
        <p className="text-gray-500 mb-8">Looks like you haven't added anything yet</p>
        <button 
          onClick={() => navigate('/dlashop')} 
          className="bg-[#70B444] text-white px-8 py-3 rounded-lg hover:bg-[#5A9437]"
        >
          Continue Shopping
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {isMember && memberData && (
        <div className="mb-6 p-4 rounded-lg" style={{ background: GREEN_LIGHT, border: `1px solid ${GREEN}` }}>
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5" style={{ color: GREEN }} />
            <p className="text-sm" style={{ color: GREEN_DARK }}>
              Signed in as <span className="font-bold">{memberData.first_name} {memberData.last_name}</span>
            </p>
          </div>
        </div>
      )}

      {isMember && memberSavings > 0 && (
        <div className="mb-6 p-4 rounded-lg" style={{ background: GREEN_LIGHT, border: `1px solid ${GREEN}` }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: GREEN_LIGHT }}>
              <Users className="w-5 h-5" style={{ color: GREEN }} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Member Savings Active!</h3>
              <p className="text-sm" style={{ color: GREEN_DARK }}>
                You're saving <span className="font-bold">${memberSavings.toFixed(2)}</span> on member-exclusive free items
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Shopping Cart ({basket.total_items} {basket.total_items === 1 ? 'item' : 'items'})
          </h1>
          {isMember && memberSavings > 0 && (
            <p className="text-sm mt-1" style={{ color: GREEN }}>
              <Users className="w-4 h-4 inline mr-1" />
              Member prices applied
            </p>
          )}
        </div>
        <button 
          onClick={() => navigate('/dlashop')} 
          className="text-gray-500 hover:text-[#70B444] flex items-center gap-1 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" /> Continue Shopping
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {basket.items.map((item) => {
            const isUpdating = updatingItems[item.id];
            const productTitle = item.product_title || 'Product';
            const productImage = item.product_thumbnail || item.product_image || null;
            const isFreeForMember = isItemFreeForMember(item);
            const itemPrice = getItemPrice(item);
            const itemShipping = getItemShipping(item);
            const originalPrice = parseFloat(item.price_at_add || 0);
            const quantity = item.quantity || 1;
            const itemPriceTotal = itemPrice * quantity;
            const itemShippingTotal = itemShipping * quantity;
            const itemGrandTotal = itemPriceTotal + itemShippingTotal;
            const hasFiles = item.product_files && item.product_files.length > 0;
            const downloadFiles = item.product_files?.filter(f => !f.is_image) || [];
            
            return (
              <div key={item.id} className="flex gap-6 border-b py-6 relative transition-opacity duration-200">
                {/* Subtle loading indicator instead of overlay */}
                {isUpdating && (
                  <div className="absolute -left-2 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#70B444] border-t-transparent"></div>
                  </div>
                )}
                
                <div className="w-24 h-24 bg-gray-50 rounded flex items-center justify-center flex-shrink-0">
                  {productImage ? (
                    <img 
                      src={productImage} 
                      alt={productTitle} 
                      className="w-20 h-20 object-contain"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.classList.add('flex', 'items-center', 'justify-center');
                      }}
                    />
                  ) : (
                    <Package className="w-8 h-8 text-gray-400" />
                  )}
                </div>
                
                <div className="flex-1">
                  <div className="flex justify-between mb-2">
                    <div>
                      <h3 className="font-medium text-gray-900">{productTitle}</h3>
                      
                      {isFreeForMember && (
                        <span 
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full mt-1"
                          style={{ background: GREEN_LIGHT, color: GREEN_DARK }}
                        >
                          <Users className="w-3 h-3" />
                          FREE for Members
                        </span>
                      )}

                      {hasFiles && (
                        <span 
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full mt-1 ml-2 bg-blue-50 text-blue-800"
                        >
                          <Download className="w-3 h-3" />
                          {item.product_files.length} file{item.product_files.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    <button 
                      onClick={() => removeItemOptimistic(item.id, item)} 
                      disabled={isUpdating}
                      className="text-gray-400 hover:text-red-500 disabled:opacity-30 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="flex items-center gap-3 text-sm mb-2">
                    {isFreeForMember ? (
                      <>
                        <span className="text-lg font-semibold text-[#70B444]">FREE</span>
                        <span className="text-sm text-gray-400 line-through">
                          {formatPrice(originalPrice)} each
                        </span>
                      </>
                    ) : (
                      <span className="text-lg font-semibold text-gray-900">
                        {formatPrice(itemPrice)} each
                      </span>
                    )}
                  </div>

                  {itemShipping > 0 && (
                    <div className="flex items-center gap-2 text-sm mb-2">
                      <Truck className="w-4 h-4 text-blue-500" />
                      <span className="text-gray-600">
                        Shipping: <span className="font-medium text-blue-600">{formatPrice(itemShipping)} per unit</span>
                      </span>
                    </div>
                  )}

                  {downloadFiles.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        <Download className="w-3 h-3" />
                        Includes {downloadFiles.length} downloadable file{downloadFiles.length > 1 ? 's' : ''}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {downloadFiles.slice(0, 2).map((file, idx) => (
                          <span 
                            key={idx}
                            className="text-xxs bg-gray-100 px-2 py-1 rounded text-gray-600"
                          >
                            {file.file_name?.substring(0, 20)}
                            {file.file_name?.length > 20 ? '...' : ''}
                          </span>
                        ))}
                        {downloadFiles.length > 2 && (
                          <span className="text-xxs text-gray-500">
                            +{downloadFiles.length - 2} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-4">
                    <div className="flex items-center border rounded">
                      <button 
                        onClick={() => updateQuantityOptimistic(item.id, item.quantity - 1, item)}
                        disabled={item.quantity <= 1 || isUpdating}
                        className="px-3 py-1 hover:bg-gray-50 disabled:opacity-30 transition-colors"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="px-3 py-1 text-sm min-w-[40px] text-center">
                        {quantity}
                      </span>
                      <button 
                        onClick={() => updateQuantityOptimistic(item.id, item.quantity + 1, item)}
                        disabled={item.quantity >= (item.product_amount || 999) || isUpdating}
                        className="px-3 py-1 hover:bg-gray-50 disabled:opacity-30 transition-colors"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="text-right min-w-[100px]">
                  {isFreeForMember ? (
                    <>
                      <p className="font-semibold text-[#70B444]">FREE</p>
                      {itemShipping > 0 && (
                        <p className="text-xs text-gray-500">
                          Shipping: {formatPrice(itemShippingTotal)}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 line-through">
                        {formatPrice(originalPrice * quantity)}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold text-gray-900">{formatPrice(itemPriceTotal)}</p>
                      {itemShipping > 0 && (
                        <p className="text-xs text-gray-500">
                          + {formatPrice(itemShippingTotal)} shipping
                        </p>
                      )}
                      <p className="text-xs font-medium text-purple-600 mt-1">
                        Total: {formatPrice(itemGrandTotal)}
                      </p>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="lg:col-span-1">
          <div className="bg-gray-50 rounded-lg p-6 sticky top-24">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>
            
            <div className="space-y-3 pb-4 border-b">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium text-gray-900">{formatPrice(displaySubtotal)}</span>
              </div>
              
              {displayTotalShipping > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600 flex items-center gap-1">
                    <Truck className="w-4 h-4 text-blue-500" />
                    Shipping Total
                  </span>
                  <span className="font-medium text-blue-600">{formatPrice(displayTotalShipping)}</span>
                </div>
              )}
              
              {isMember && memberSavings > 0 && (
                <div className="flex justify-between" style={{ color: GREEN }}>
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    Member Savings
                  </span>
                  <span className="font-medium">-{formatPrice(memberSavings)}</span>
                </div>
              )}
              
              {displayTotalShipping === 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Shipping</span>
                  <span className="font-medium text-gray-900">Free</span>
                </div>
              )}
            </div>
            
            <div className="pt-4">
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold text-gray-900">Grand Total</span>
                <span className="text-xl font-bold text-[#70B444]">{formatPrice(displayGrandTotal)}</span>
              </div>
              
              {displayTotalShipping > 0 && (
                <p className="text-xs text-gray-500 mb-4 text-right">
                  Includes ${displayTotalShipping.toFixed(2)} shipping
                </p>
              )}
              
              <button 
                onClick={() => {
                  Swal.fire({
                    title: 'Checkout Coming Soon',
                    html: `<div class="text-left">
                      <p>Your order total is <span class="font-bold text-[#70B444]">${formatPrice(displayGrandTotal)}</span></p>
                      ${memberSavings > 0 ? `<p class="text-sm text-[#70B444] mt-2">You saved $${memberSavings.toFixed(2)} as a member!</p>` : ''}
                      <p class="text-sm text-gray-600 mt-2">Checkout functionality will be available soon!</p>
                    </div>`,
                    icon: 'info',
                    confirmButtonColor: GREEN
                  });
                }}
                className="w-full bg-[#70B444] text-white py-3 rounded-lg font-medium hover:bg-[#5A9437] transition-colors"
              >
                Proceed to Checkout
              </button>
              
              {!isMember && basket.items.some(item => item.is_free_for_members_at_add === true) && (
                <div className="mt-4 p-3 rounded-lg" style={{ background: GREEN_LIGHT }}>
                  <p className="text-xs text-center" style={{ color: GREEN_DARK }}>
                    <button 
                      onClick={() => navigate('/member-login')}
                      className="font-bold hover:underline"
                      style={{ color: GREEN }}
                    >
                      Sign in as member
                    </button> to get free items
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .text-xxs {
          font-size: 0.625rem;
          line-height: 0.75rem;
        }
      `}</style>
    </div>
  );
};

export default Checkout;